import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { adminApi } from '../api'

/** 숫자 배지를 띄우는 항목들 */
type AdminBadgeKey =
  | 'inquiries'
  | 'reports'
  | 'keywordSuggestions'
  | 'placeSuggestions'

interface AdminSidebarItem {
  id: string
  label: string
  path: string
  /** 대기 건수처럼 숫자를 띄울 항목 */
  badge?: AdminBadgeKey
  /** 새로 들어온 게 있으면 빨간 점만 띄울 항목 (숫자는 안 센다) */
  dot?: AdminDotKey
}

/** 빨간 점을 띄우는 항목들 */
type AdminDotKey = 'reviews' | 'users'

/** 큰 메뉴 하나. 누르면 세부 메뉴가 펼쳐진다 */
interface AdminSidebarGroup {
  id: string
  label: string
  items: AdminSidebarItem[]
}

/*
 * 메뉴는 하는 일 기준으로 묶는다. 주소는 그대로라 즐겨찾기·링크가 깨지지 않는다.
 * 고객 대응은 매일 볼 것(문의·신고·제안)이라 지표 바로 아래에 둔다.
 */
const ADMIN_SIDEBAR_GROUPS: AdminSidebarGroup[] = [
  {
    id: 'metrics',
    label: '지표',
    items: [
      { id: 'stats', label: '지표', path: '/admin/stats' },
      { id: 'app-versions', label: '앱 버전', path: '/admin/app-versions' },
    ],
  },
  {
    id: 'support',
    label: '고객 대응',
    items: [
      { id: 'inquiries', label: '문의 관리', path: '/admin/inquiries', badge: 'inquiries' },
      { id: 'reports', label: '신고 관리', path: '/admin/reports', badge: 'reports' },
      {
        id: 'review-keyword-suggestions',
        label: '키워드 제안 관리',
        path: '/admin/review-keyword-suggestions',
        badge: 'keywordSuggestions',
      },
      {
        id: 'place-suggestions',
        label: '장소 제안 관리',
        path: '/admin/place-suggestions',
        badge: 'placeSuggestions',
      },
    ],
  },
  {
    id: 'members',
    label: '회원',
    items: [{ id: 'users', label: '사용자 관리', path: '/admin/users', dot: 'users' }],
  },
  {
    id: 'places',
    label: '장소',
    items: [
      { id: 'places', label: '장소 관리', path: '/admin/places' },
      { id: 'place-names', label: '이름 어긋난 장소', path: '/admin/place-names' },
      { id: 'place-categories', label: '장소 카테고리 관리', path: '/admin/place-categories' },
      { id: 'map-settings', label: '지도 설정', path: '/admin/map-settings' },
    ],
  },
  {
    id: 'records',
    label: '기록',
    items: [
      { id: 'reviews', label: '리뷰 관리', path: '/admin/reviews', dot: 'reviews' },
      { id: 'review-keywords', label: '리뷰 키워드 관리', path: '/admin/review-keywords' },
      {
        id: 'default-review-categories',
        label: '기본 리뷰 카테고리 관리',
        path: '/admin/default-review-categories',
      },
    ],
  },
  {
    id: 'messaging',
    label: '알림·홍보',
    items: [
      { id: 'push', label: '광고 푸시 발송', path: '/admin/push' },
      { id: 'notices', label: '공지사항 관리', path: '/admin/notices' },
      { id: 'curated-feed', label: '가입 직후 추천 피드', path: '/admin/curated-feed' },
      { id: 'policies', label: '약관 관리', path: '/admin/policies' },
    ],
  },
  {
    id: 'dev',
    label: '개발 도구',
    items: [
      { id: 'instagram-test', label: '인스타 가져오기 테스트', path: '/admin/instagram-test' },
      { id: 'toast-preview', label: '토스트 미리보기', path: '/admin/toast-preview' },
    ],
  },
]

/** 개발 도구만 평소 접어 둔다. 나머지는 처음에 다 펼쳐 둔다 */
const COLLAPSED_BY_DEFAULT = ['dev']

/** 큰 메뉴를 접었는지 브라우저에 기억한다 (관리자 각자) */
const OPEN_GROUPS_KEY = 'admin_sidebar_collapsed_groups'

const readCollapsed = (): string[] => {
  try {
    const raw = localStorage.getItem(OPEN_GROUPS_KEY)
    if (raw === null) return COLLAPSED_BY_DEFAULT
    const v = JSON.parse(raw)
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : COLLAPSED_BY_DEFAULT
  } catch {
    return COLLAPSED_BY_DEFAULT
  }
}

const writeCollapsed = (ids: string[]): void => {
  try {
    localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(ids))
  } catch {
    // 저장이 막혀도 이번 화면에서는 접고 펼 수 있다
  }
}

type AdminBadgeCounts = Record<AdminBadgeKey, number>

const EMPTY_BADGE_COUNTS: AdminBadgeCounts = {
  inquiries: 0,
  reports: 0,
  keywordSuggestions: 0,
  placeSuggestions: 0,
}

/*
 * 배지를 세는 방법이 항목마다 다르다 — 셀 수 있는 것이 다르기 때문이다.
 *
 *  · 문의, 장소 제안
 *      서버에 처리 상태가 있다. '대기'만 세면 되고, 처리하면 저절로 줄어든다.
 *      여러 관리자가 봐도 같은 숫자가 보인다.
 *
 *  · 신고, 키워드 제안
 *      테이블에 상태 칸이 아예 없고 화면도 읽기만 한다. 서버에 물어볼
 *      '대기 건수'라는 게 없다. 그래서 '내가 마지막으로 그 화면을 봤을 때의
 *      전체 건수'를 적어두고, 지금 전체와의 차이를 새로 들어온 것으로 센다.
 *
 * 뒤의 둘은 브라우저에 적으므로 관리자가 여럿이면 각자 따로 센다. 지금은
 * 한 명이라 괜찮다. 여럿이 되거나 '확인함'을 남겨야 하면, 두 테이블에
 * 처리 상태 칸을 붙이고 앞의 방식으로 옮기는 게 맞다.
 */
const SEEN_TOTAL_KEY = {
  reports: 'admin_seen_total_reports',
  keywordSuggestions: 'admin_seen_total_keyword_suggestions',
} as const

const readSeenTotal = (key: string): number | null => {
  try {
    const raw = localStorage.getItem(key)
    if (raw === null) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

const writeSeenTotal = (key: string, total: number): void => {
  try {
    localStorage.setItem(key, String(total))
  } catch {
    // 사파리 프라이빗 등 저장이 막힌 경우. 배지가 안 줄어들 뿐이니 넘어간다.
  }
}

/**
 * 마지막으로 본 뒤 새로 들어온 건수.
 *
 * 그 화면을 보고 있으면 지금 수를 기준으로 다시 적고 0 을 돌려준다 —
 * 들어가서 보면 배지가 사라져야 한다.
 *
 * 처음 켠 브라우저에는 기준이 없다. 그때 전체를 그대로 새 것으로 세면
 * 쌓여 있던 수백 건이 전부 빨갛게 뜬다. 기준이 없으면 지금 수를 기준으로
 * 삼고 0 에서 시작한다.
 */
const countNewSinceSeen = (
  key: string,
  total: number,
  isOnThatPage: boolean
): number => {
  if (isOnThatPage) {
    writeSeenTotal(key, total)
    return 0
  }
  const seen = readSeenTotal(key)
  if (seen === null) {
    writeSeenTotal(key, total)
    return 0
  }
  return Math.max(0, total - seen)
}

/*
 * 새 리뷰·새 가입자는 숫자 대신 빨간 점만 띄운다. 하루에도 여러 건이라
 * 숫자를 세면 늘 떠 있는 배지가 되고, 할 일이 아니라 '새 소식'이기 때문이다.
 *
 * 전체 건수가 아니라 **가장 최근 id** 로 비교한다. 건수로 세면 그 사이
 * 탈퇴·삭제가 있을 때 새로 들어온 것이 지워진 것과 상쇄돼 놓친다.
 * 두 목록 모두 최신순이라 1건만 받으면 가장 최근 id 를 알 수 있다.
 */
const SEEN_LATEST_ID_KEY: Record<AdminDotKey, string> = {
  reviews: 'admin_seen_latest_review_id',
  users: 'admin_seen_latest_user_id',
}

/** 그 화면을 본 뒤 새로 들어온 게 있는지. 보고 있으면 지금 것을 기준으로 적는다 */
const hasNewSinceSeen = (
  key: string,
  latestId: number | null,
  isOnThatPage: boolean
): boolean => {
  if (latestId === null) return false
  const seen = readSeenTotal(key)
  // 처음 켠 브라우저에는 기준이 없다 — 지금 것을 기준으로 삼고 점은 안 띄운다
  if (isOnThatPage || seen === null) {
    writeSeenTotal(key, latestId)
    return false
  }
  return latestId > seen
}

const AdminSidebar: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()

  /*
   * 할 일이 쌓인 항목에 숫자를 띄운다.
   *
   * 사이드바는 모든 관리자 화면에 깔려 있어서, 어느 화면에 있든 새 문의·신고·제안이
   * 들어온 걸 알 수 있어야 한다. 목록 API 를 1건만 받아 집계 값만 쓴다 — 전용
   * 엔드포인트를 따로 만들 만큼 무거운 일이 아니다. 넷을 한 번에 보낸다.
   *
   * 하나가 실패하면 그 배지만 안 그린다. 사이드바가 깨지거나 나머지 숫자까지
   * 사라지는 것보다 그게 낫다.
   */
  const [counts, setCounts] = useState<AdminBadgeCounts>(EMPTY_BADGE_COUNTS)
  const [dots, setDots] = useState<Record<AdminDotKey, boolean>>({
    reviews: false,
    users: false,
  })

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) return
    let alive = true
    const pathname = location.pathname

    const load = async () => {
      const [
        inquiries,
        reports,
        keywordSuggestions,
        placeSuggestions,
        latestReviews,
        latestUsers,
      ] = await Promise.all([
        adminApi.getInquiries({ per_page: 1 }).catch(() => null),
        adminApi.getReports({ per_page: 1 }).catch(() => null),
        adminApi.getReviewKeywordSuggestions({ per_page: 1 }).catch(() => null),
        adminApi.getPlaceSuggestions({ per_page: 1 }).catch(() => null),
        adminApi.getReviews({ per_page: 1 }).catch(() => null),
        adminApi.getUsers({ per_page: 1 }).catch(() => null),
      ])
      if (!alive) return

      setDots({
        reviews: hasNewSinceSeen(
          SEEN_LATEST_ID_KEY.reviews,
          latestReviews?.data?.[0]?.id ?? null,
          pathname === '/admin/reviews'
        ),
        users: hasNewSinceSeen(
          SEEN_LATEST_ID_KEY.users,
          latestUsers?.data?.[0]?.id ?? null,
          pathname === '/admin/users'
        ),
      })

      setCounts({
        inquiries: inquiries ? inquiries.counts.open : 0,
        reports: reports
          ? countNewSinceSeen(
              SEEN_TOTAL_KEY.reports,
              reports.total,
              pathname === '/admin/reports'
            )
          : 0,
        keywordSuggestions: keywordSuggestions
          ? countNewSinceSeen(
              SEEN_TOTAL_KEY.keywordSuggestions,
              keywordSuggestions.total,
              pathname === '/admin/review-keyword-suggestions'
            )
          : 0,
        placeSuggestions: placeSuggestions
          ? placeSuggestions.status_counts.pending
          : 0,
      })
    }

    load()
    return () => {
      alive = false
    }
    // 화면을 옮길 때마다 다시 센다 — 처리하고 나오면 숫자가 줄어 있어야 한다
  }, [location.pathname])

  const handleNavClick = (path: string) => {
    navigate(path)
  }

  /*
   * 접힌 큰 메뉴들. 화면에 들어올 때(사이드바는 화면마다 새로 그려진다) 지금 화면이
   * 든 큰 메뉴만 한 번 펼쳐 준다. 그 뒤에는 그 메뉴도 자유롭게 접을 수 있다 —
   * 예전에는 지금 화면이 든 메뉴를 항상 펼쳐 둬서 닫히지 않았다.
   */
  const [collapsed, setCollapsed] = useState<string[]>(() => {
    const activeGroup = ADMIN_SIDEBAR_GROUPS.find((g) =>
      g.items.some((item) => location.pathname === item.path)
    )
    return readCollapsed().filter((id) => id !== activeGroup?.id)
  })

  const toggleGroup = (id: string) => {
    setCollapsed((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      writeCollapsed(next)
      return next
    })
  }

  const handleLogout = () => {
    localStorage.removeItem('admin_token')
    navigate('/admin/login')
  }

  return (
    <aside className="w-240px bg-white border-r border-brandPrimary-10% min-h-screen sticky top-0 flex flex-col">
      <div className="p-24px flex-1">
        <div className="mb-32px">
          <h1 className="text-style-title1 text-brown-80%-main">TASTEE</h1>
          <span className="text-style-body2 text-textColorText2">관리자</span>
        </div>

        <nav className="flex flex-col gap-4px">
          {ADMIN_SIDEBAR_GROUPS.map((group) => {
            const isOpen = !collapsed.includes(group.id)
            const groupBadge = group.items.reduce(
              (sum, item) => sum + (item.badge ? counts[item.badge] : 0),
              0
            )
            const groupDot = group.items.some((item) => item.dot && dots[item.dot])

            return (
              <div key={group.id} className="flex flex-col gap-2px">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  aria-expanded={isOpen}
                  className="text-left py-10px px-12px rounded-8px text-style-button2 text-textColorText3 hover:bg-backgroundColorBg2 transition-colors"
                >
                  <span className="flex items-center justify-between gap-8px">
                    <span className="flex items-center gap-6px">
                      <span
                        className={`inline-block text-10px transition-transform ${
                          isOpen ? 'rotate-90' : ''
                        }`}
                        aria-hidden
                      >
                        ▶
                      </span>
                      {group.label}
                    </span>
                    {/* 접혀 있을 때만 합계를 띄운다 — 펼치면 항목마다 보인다 */}
                    {!isOpen && groupBadge > 0 && (
                      <span className="flex-shrink-0 min-w-20px px-7px py-2px rounded-full bg-semanticError text-white text-center text-style-captionM leading-16px">
                        {groupBadge > 99 ? '99+' : groupBadge}
                      </span>
                    )}
                    {!isOpen && groupBadge === 0 && groupDot && (
                      <span
                        className="flex-shrink-0 w-8px h-8px rounded-full bg-semanticError"
                        aria-label="새 소식"
                      />
                    )}
                  </span>
                </button>

                {isOpen &&
                  group.items.map((item) => {
                    const isActive = location.pathname === item.path
                    const badgeCount = item.badge ? counts[item.badge] : 0

                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => handleNavClick(item.path)}
                        className={`
                          text-left
                          py-10px
                          pl-28px
                          pr-16px
                          rounded-8px
                          text-style-body1
                          transition-colors
                          ${
                            isActive
                              ? 'bg-4B3621-80%-10% text-brown-80%-main font-medium'
                              : 'text-textColorText2 hover:bg-backgroundColorBg2'
                          }
                        `}
                      >
                        <span className="flex items-center justify-between gap-8px">
                          <span>{item.label}</span>
                          {badgeCount > 0 && (
                            <span className="flex-shrink-0 min-w-20px px-7px py-2px rounded-full bg-semanticError text-white text-center text-style-captionM leading-16px">
                              {badgeCount > 99 ? '99+' : badgeCount}
                            </span>
                          )}
                          {item.dot && dots[item.dot] && (
                            <span
                              className="flex-shrink-0 w-8px h-8px rounded-full bg-semanticError"
                              aria-label="새로 들어온 것이 있음"
                            />
                          )}
                        </span>
                      </button>
                    )
                  })}
              </div>
            )
          })}
        </nav>
      </div>

      <div className="p-24px border-t border-brandPrimary-10%">
        <button
          type="button"
          onClick={handleLogout}
          className="w-full text-left py-12px px-16px rounded-8px text-style-body1 text-semanticError hover:bg-backgroundColorBg2 transition-colors"
        >
          로그아웃
        </button>
      </div>
    </aside>
  )
}

export default AdminSidebar
